import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  Camera,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Link2,
  MoreHorizontal,
  UserCheck,
} from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ORIGIN_LABELS,
  OPEN_STATUSES,
  useChangeRequestStatus,
  useDeleteRequest,
  type ProductRequest,
} from '@/hooks/useProductRequests'
import { fmtDate } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/authStore'
import { PhotoLightbox } from './PhotoLightbox'
import { DemandBadge, RequestStatusBadge } from './RequestStatusBadge'

// Row handlers live on the table (not in a store) because every one of them
// opens a modal that T6 owns; the page wires them in one place.
interface RequestTableProps {
  data: ProductRequest[]
  onEdit: (row: ProductRequest) => void
  onAddToPO: (row: ProductRequest) => void
  onCustomerClick: (row: ProductRequest) => void
  onClearFilters?: () => void
}

const HandlerContext = React.createContext<
  Pick<RequestTableProps, 'onEdit' | 'onAddToPO' | 'onCustomerClick'> | null
>(null)

function useHandlers() {
  const ctx = React.useContext(HandlerContext)
  if (!ctx) throw new Error('RequestTable HandlerContext missing')
  return ctx
}

const OPEN_STATUS_SET = new Set<string>(OPEN_STATUSES)

// Add to PO is offered while the request is still un-sourced/sourced, and again
// on an ORDERED request whose PO got archived — that PO is closed out, so the
// only way to re-order the item is onto a fresh one.
const ADD_TO_PO_STATUSES = new Set(['OPEN', 'NEED_TO_SOURCE', 'SOURCED'])
const RESOLVED_STATUSES = new Set(['PURCHASED', 'CLOSED_NO_PURCHASE'])

function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function CustomerCell({ req }: { req: ProductRequest }) {
  const { onCustomerClick } = useHandlers()
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-xs text-[var(--text-primary)]">{req.customer_name}</span>
      {req.shopify_customer_id && (
        <button
          type="button"
          onClick={() => onCustomerClick(req)}
          className="text-[var(--brand)] hover:opacity-70"
          aria-label={`View Shopify customer ${req.customer_name}`}
          title="Matched Shopify customer — view order history"
        >
          <UserCheck className="h-3 w-3" />
        </button>
      )}
    </span>
  )
}

/** The camera is the only way into the photo from the board, so it opens it. */
function PhotoCell({ req }: { req: ProductRequest }) {
  const [open, setOpen] = React.useState(false)
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setOpen(true)
        }}
        className="text-[var(--mute)] hover:text-[var(--text-primary)]"
        aria-label={`View photo for ${req.product_title}`}
        title="Photo attached — click to view"
      >
        <Camera className="h-3 w-3" />
      </button>
      <PhotoLightbox requestId={req.id} open={open} onOpenChange={setOpen} />
    </>
  )
}

function ProductCell({ req }: { req: ProductRequest }) {
  // on_hand only means something when the request is pinned to a real variant;
  // a free-text "black breeches, size 28" has no stock to be out of.
  const outOfStock = req.variant_id != null && req.on_hand === 0
  // Logged in one submission with siblings — the rows are independent, so this
  // is context only, not a grouping of the table.
  const grouped = req.request_group_id != null && req.group_count >= 2
  return (
    <div className="flex flex-col gap-0.5">
      <span className="inline-flex items-center gap-1.5">
        <span className="text-xs text-[var(--text-primary)]">{req.product_title}</span>
        {grouped && (
          <span
            title={`Logged together with ${req.group_count - 1} other item(s)`}
            aria-label={`Logged together with ${req.group_count - 1} other item(s)`}
          >
            <Link2 className="h-3 w-3 text-[var(--mute)]" />
          </span>
        )}
        {req.has_photo && <PhotoCell req={req} />}
        {req.demand_count >= 2 && <DemandBadge count={req.demand_count} />}
      </span>
      {(req.sku || req.variant_title || outOfStock) && (
        <span className="inline-flex items-center gap-1.5">
          {req.variant_title && (
            <span className="text-[11px] text-[var(--mute)]">{req.variant_title}</span>
          )}
          {req.variant_title && req.sku && (
            <span className="text-[11px] text-[var(--mute)]">·</span>
          )}
          {req.sku && (
            <span className="font-mono text-[11px] text-[var(--mute)]">{req.sku}</span>
          )}
          {outOfStock && (
            <span className="text-[11px] font-medium text-[var(--mute)]">Out of stock</span>
          )}
        </span>
      )}
    </div>
  )
}

function FollowUpCell({ req }: { req: ProductRequest }) {
  if (!req.follow_up_date) {
    return <span className="text-xs text-[var(--mute)]">—</span>
  }
  // Overdue only matters while the request is still live — a PURCHASED request
  // with a stale follow-up date isn't an action item.
  const overdue =
    req.follow_up_date < todayIso() && OPEN_STATUS_SET.has(req.status)
  return (
    <span
      className={cn(
        'text-xs tabular-nums whitespace-nowrap',
        overdue ? 'font-semibold text-[var(--status-rejected-fg)]' : 'text-[var(--mute)]'
      )}
      title={overdue ? 'Follow-up is overdue' : undefined}
    >
      {fmtDate(req.follow_up_date)}
    </span>
  )
}

function ContextMenu({ req }: { req: ProductRequest }) {
  const { onEdit, onAddToPO } = useHandlers()
  const changeStatus = useChangeRequestStatus()
  const deleteMutation = useDeleteRequest()
  const user = useAuthStore((s) => s.user)

  const canDelete =
    user != null &&
    (user.username?.toLowerCase() === 'gina' || user.role === 'admin')
  const canAddToPO =
    ADD_TO_PO_STATUSES.has(req.status) ||
    (req.status === 'ORDERED' && req.po_status === 'ARCHIVED')
  const isResolved = RESOLVED_STATUSES.has(req.status)

  function handleMarkPurchased() {
    changeStatus.mutate(
      { id: req.id, status: 'PURCHASED' },
      {
        onSuccess: () =>
          toast.success(`${req.customer_name}'s request marked purchased`),
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Failed to update status'),
      }
    )
  }

  function handleDelete() {
    toast(`Delete ${req.customer_name}'s request for ${req.product_title}?`, {
      action: {
        label: 'Delete',
        onClick: () => {
          deleteMutation.mutate(req.id, {
            onSuccess: () => toast.success('Request deleted'),
            onError: (err) =>
              toast.error(err instanceof Error ? err.message : 'Failed to delete request'),
          })
        },
      },
    })
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 p-0 text-[var(--mute)] hover:text-[var(--text-primary)]"
          aria-label="Row actions"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onSelect={() => onEdit(req)}>Edit</DropdownMenuItem>

        {canAddToPO && (
          <DropdownMenuItem onSelect={() => onAddToPO(req)}>Add to PO</DropdownMenuItem>
        )}

        {!isResolved && (
          <DropdownMenuItem
            onSelect={handleMarkPurchased}
            disabled={changeStatus.isPending}
          >
            Customer Purchase
          </DropdownMenuItem>
        )}

        {canDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive"
              onSelect={handleDelete}
              disabled={deleteMutation.isPending}
            >
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Column order matches the spec: Date | Customer | Product | Qty | Source |
// Assigned To | Follow Up | Status | ⋮
const COLUMNS: ColumnDef<ProductRequest>[] = [
  {
    accessorKey: 'created_at',
    id: 'created_at',
    header: 'Date',
    cell: ({ row }) => (
      <span className="text-xs tabular-nums whitespace-nowrap text-[var(--mute)]">
        {fmtDate(row.original.created_at) || '—'}
      </span>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'customer_name',
    id: 'customer_name',
    header: 'Customer',
    cell: ({ row }) => <CustomerCell req={row.original} />,
    enableSorting: true,
  },
  {
    accessorKey: 'product_title',
    id: 'product_title',
    header: 'Product',
    cell: ({ row }) => <ProductCell req={row.original} />,
    enableSorting: true,
  },
  {
    accessorKey: 'qty',
    id: 'qty',
    header: 'Qty',
    cell: ({ row }) => (
      <span className="text-xs tabular-nums text-[var(--ink-2)]">{row.original.qty}</span>
    ),
    enableSorting: true,
    meta: { numeric: true },
  },
  {
    accessorKey: 'originated_from',
    id: 'originated_from',
    header: 'Source',
    cell: ({ row }) => (
      <div className="flex flex-col gap-0.5">
        <span className="text-xs text-[var(--ink-2)]">
          {ORIGIN_LABELS[row.original.originated_from] ?? row.original.originated_from}
        </span>
        {row.original.originated_from === 'EVENT' && row.original.event_location_name && (
          <span className="text-[11px] text-[var(--mute)]">
            {row.original.event_location_name}
          </span>
        )}
      </div>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'assigned_to',
    id: 'assigned_to',
    header: 'Assigned To',
    cell: ({ row }) => (
      <span className="text-xs text-[var(--ink-2)]">{row.original.assigned_to || '—'}</span>
    ),
    enableSorting: true,
  },
  {
    accessorKey: 'follow_up_date',
    id: 'follow_up_date',
    header: 'Follow Up',
    cell: ({ row }) => <FollowUpCell req={row.original} />,
    enableSorting: true,
  },
  {
    accessorKey: 'status',
    id: 'status',
    header: 'Status',
    cell: ({ row }) => <RequestStatusBadge status={row.original.status} />,
    enableSorting: false,
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => <ContextMenu req={row.original} />,
    enableSorting: false,
    size: 40,
  },
]

export function RequestTable({
  data,
  onEdit,
  onAddToPO,
  onCustomerClick,
  onClearFilters,
}: RequestTableProps) {
  // The API already returns newest-first; this seeds the header arrow to match
  // so the visible sort state isn't a lie.
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'created_at', desc: true },
  ])

  const handlers = React.useMemo(
    () => ({ onEdit, onAddToPO, onCustomerClick }),
    [onEdit, onAddToPO, onCustomerClick]
  )

  const table = useReactTable({
    data,
    columns: COLUMNS,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm text-[var(--mute)]">No product requests match these filters</p>
        {onClearFilters && (
          <Button variant="outline" size="sm" className="mt-4 text-xs" onClick={onClearFilters}>
            Clear filters
          </Button>
        )}
      </div>
    )
  }

  return (
    <HandlerContext.Provider value={handlers}>
      <div className="overflow-auto flex-1">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="hover:bg-transparent border-[var(--line-warm)]">
                {hg.headers.map((header) => {
                  const numeric =
                    (header.column.columnDef.meta as { numeric?: boolean } | undefined)
                      ?.numeric ?? false
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        'sticky top-0 z-10',
                        numeric && 'text-right',
                        header.column.getCanSort() && 'cursor-pointer select-none'
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span
                        className={cn(
                          'inline-flex items-center gap-1',
                          numeric && 'justify-end w-full'
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span className="opacity-50">
                            {header.column.getIsSorted() === 'asc' ? (
                              <ChevronUp className="h-3 w-3" />
                            ) : header.column.getIsSorted() === 'desc' ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronsUpDown className="h-3 w-3" />
                            )}
                          </span>
                        )}
                      </span>
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row, index) => (
              <TableRow
                key={row.id}
                className={cn(
                  'border-b border-[var(--line-warm)] hover:bg-[var(--surface-header)]',
                  index % 2 === 1
                    ? 'bg-[var(--surface-zebra)]'
                    : 'bg-[var(--surface-card)]'
                )}
              >
                {row.getVisibleCells().map((cell) => {
                  const numeric =
                    (cell.column.columnDef.meta as { numeric?: boolean } | undefined)
                      ?.numeric ?? false
                  return (
                    <TableCell
                      key={cell.id}
                      className={cn('py-1.5 px-2 text-xs', numeric && 'text-right tabular-nums')}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </HandlerContext.Provider>
  )
}
